/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';

import { css } from '@emotion/css';
import { format } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { borders, COLORS2_AS_FOREGROUND, font2, MARGINS } from '^/constants/commonStyles';
import { ReservationDetailed } from '^/types/ReservationDetailed/RawReservationDetailed';
import { formatKRWWonAmount } from '^/util/formatKRWWonAmount';

import { Horizon } from '../../../../ReservationList/components/common/Horizon';

import { ReservationDetailsMobileBillPODArea } from './ReservationDetailsMobile.Bill.PODArea';
import { ReservationDetailsMobileBillPPDArea } from './ReservationDetailsMobile.Bill.PPDArea';
import { CancellationWarningBeforePickup72Hours } from '^/pages/ReservationDetail/CancellationWarningBeforePickup72hours';
import { formatDateString } from '^/util/dateFormat';
import { ExtraChargeDisplayCurrency } from '^/pages/ReservationDetail/extraChargeDisplay';

const wonCurrency = 'KRW';

export const ReservationDetailsBill: React.FC<{
  data: ReservationDetailed;
  onClickCancelReservation(reservationToCancel: ReservationDetailed): void;
  extraChargeDisplayCurrency?: ExtraChargeDisplayCurrency;
}> = observer(function ReservationDetailsBill({
  data,
  onClickCancelReservation,
  extraChargeDisplayCurrency = 'foreign',
}) {
  const { t } = useTranslation();
  return (
    <div className={[COLORS2_AS_FOREGROUND.main.MrtBlack].join(' ')}>
      <pre
        style={{
          color: 'blue',
          backgroundColor: 'yellow',
          display: 'none',
        }}
      >
        {JSON.stringify(data, null, 2)}
      </pre>
      <h3 className={font2.Headline1_Bold}>
        {data.res_status === 'booked' ? '요금 내역' : '취소 내역'}
      </h3>

      {data.pay_type === 'POD' ? (
        <ReservationDetailsMobileBillPODArea
          data={data}
          extraChargeDisplayCurrency={extraChargeDisplayCurrency}
        />
      ) : (
        <ReservationDetailsMobileBillPPDArea
          data={data}
          extraChargeDisplayCurrency={extraChargeDisplayCurrency}
        />
      )}
      {data.res_status === 'canceled' && (
        <>
          <div className="mt-4 flex flex-row  justify-between">
            <p className="text-semilarge mobile:text-big">{t('cancelDate')}</p>
            <p className="text-semilarge font-bold">
              {formatDateString(data.date_cancel, 'YYYY.MM.DD HH:mm (z)', 'Asia/Seoul') || '-'}
            </p>
          </div>
        </>
      )}
      {data.res_status === 'canceled' && data.pay_type === 'PPD' && (
        <>
          <Horizon marginTop="16px" marginBottom="16px" />
          <div className="mt-4 flex flex-row justify-between tablet:mt-0">
            <p className="text-big tablet:text-semilarge font-bold">환불 금액</p>
            <p className="text-semilarge font-bold">
              {formatKRWWonAmount(data.refund_price)} 원
            </p>
          </div>
          <div className="mt-4 flex flex-row  justify-between">
            <p className="text-semilarge mobile:text-big font-bold">취소 수수료</p>
            <p className="text-semilarge font-bold">
              {formatKRWWonAmount(data.cancel_fee)} 원
            </p>
          </div>
          <div
            className={[
              font2.Caption2_Light,
              COLORS2_AS_FOREGROUND.main.MrtBlack,
              css`
                text-align: center;
                white-space: pre-line;
                margin-top: 15px;
              `,
            ].join(' ')}
          >
            {`취소 시 환불은 카드사를 통해 2~5일 내 환불될 예정입니다.\n실제 적용되는 취소수수료 및 환불금액과 다르게 보일 수 있습니다.`}
          </div>
        </>
      )}
      {data.res_status === 'booked' && (
        <div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault(); // 스크롤 상단 이동 방지
              onClickCancelReservation(data);
            }}
            disabled={data.cancelDisabled}
            className="
              mt-4 w-full rounded-trimo-5
              bg-trimo-bg p-3
            text-center font-bold text-primary-gray
              hover:cursor-pointer hover:bg-primary-skyblue hover:text-primary-white
            focus:text-white
              active:text-white
            disabled:cursor-not-allowed disabled:bg-secondary-gray disabled:text-neutral-300"
          >
            {t('cancelBooking')}
          </button>
          <CancellationWarningBeforePickup72Hours show={data.show72HoursCancelWarning} />
        </div>
      )}
    </div>
  );
});
